/* global TPDirect */
import React, { useEffect, useMemo, useState } from 'react';
import {
  makeStyles, Button, FormControlLabel, Radio, RadioGroup, withStyles, Box, ButtonBase,
} from '@material-ui/core';
import _, {
  isEmpty, max, min, size, sumBy,
} from 'lodash';
import { useHistory } from 'react-router-dom';
import { useAuthState } from '@/plugins/Auth';
import config from '@/configs/config';
import firebase from '@/services/firebase';
import useFirebaseDatabaseQuery from '@/hooks/useFirebaseDatabaseQuery';
import { CreditCardField } from './productDetailModal/PreOrder';
import { PAYMENT_METHOD } from '../../preorder/containers/CheckoutPage/components/Checkout';
import Alert from '../../preorder/components/Alert';
import CouponSelector from '../containers/CouponSelector';
import calculatorIcon from '../images/calculator.png';
import DiscountModal from './shoppingCart/DiscountModal';
import LocationGroup from './shoppingCart/LocationGroup';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    paddingBottom: theme.spacing(17),
    textAlign: 'left',
  },
  checkboxContainer: { alignSelf: 'center' },
  couponContainer: {
    alignItems: 'center',
    display: 'flex',
    flexBasis: '100%',
    fontSize: 13,
    justifyContent: 'space-between',
    padding: theme.spacing(0, 2),
    height: 50,

    [theme.breakpoints.up('sm')]: {
      fontSize: 16,
      padding: 0,
    },
  },
  couponText: {
    flexShrink: 0,
    fontWeight: 'bold',
  },
  couponField: {
    alignItems: 'center',
    display: 'flex',
    marginLeft: theme.spacing(1),

    '& > *+*': {
      marginLeft: theme.spacing(1),
    },
  },
  selectCouponButton: {
    flexShrink: 0,
    minWidth: 0,
    padding: 0,
    textDecoration: 'underline',
  },
  priceContainer: {
    alignSelf: 'center',
    flexBasis: 0,
    flexGrow: 1,
    fontSize: 13,
    fontWeight: 'bold',
    textAlign: 'right',
    height: 50,

    [theme.breakpoints.up('sm')]: {
      alignItems: 'center',
      display: 'flex',
      fontSize: 16,
      justifyContent: 'space-between',
      marginTop: theme.spacing(1),
      width: '100%',
    },
  },
  paymentHint: {
    fontSize: 12,
    width: 'max-content',
    marginLeft: 30,
    marginTop: -10,
  },
}));

const StyledButton = withStyles({
  root: {
    color: 'white',
    fontSize: 14,
    fontWeight: 'bold',
    borderRadius: 8,
    height: 28,
    backgroundColor: 'rgb(45,152,37)',
    '&:hover': {
      backgroundColor: 'rgb(45,152,37) !important',
      '&.Mui-disabled': {
        color: 'white',
        backgroundColor: 'rgb(216,216,216) !important',
      },
    },
    '&.Mui-disabled': {
      color: 'white',
      backgroundColor: 'rgb(216,216,216)',
    },
  },
  contained: {
    boxShadow: 'none',
  },
})(Button);

const StyledRadio = withStyles(() => ({
  root: { '&$checked': { color: '#009687' } },
  checked: {},
}))(Radio);

export default ({
  data,
  items,
  dataToDisplay,
  shopCampaignDiscounts,
  onCheckoutButtonClick: propsOnCheckoutButtonClick,
}) => {
  const classes = useStyles();
  const { user } = useAuthState();

  const [isDiscountModalOpened, setIsDiscountModalOpened] = useState(false);
  const hasUserVerifiedEnterpriseCard = useMemo(() => !!_.get(user, ['enterpriseCardVerified'], null), [user]);
  const enterpriseCardPayFridgesRef = firebase.database().ref(`/${process.env.REACT_APP_MODE}/menu/cache/enterpriseCardPayFridges`);
  const {
    isLoading: isEnterpriseCardPayFridgesLoading,
    data: enterpriseCardPayFridges,
  } = useFirebaseDatabaseQuery(enterpriseCardPayFridgesRef);
  const canUseEnterpriseCardPay = useMemo(() => (
    !!config.enableEnterpriseCardPay
    && !isEnterpriseCardPayFridgesLoading
    && (data || []).reduce((result, item) => {
      if (!result) return false;
      const fridgeId = _.get(item, ['data', 'fridgeId'], null);
      if (!enterpriseCardPayFridges.find((d) => d === fridgeId)) return false;
      return true;
    }, true)
  ), [data, isEnterpriseCardPayFridgesLoading, enterpriseCardPayFridges]);

  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHOD.TAP_PAY);
  const [alert, setAlert] = React.useState(undefined);
  const [isCouponSelectorOpen, setIsCouponSelectorOpen] = useState(false);
  const [selectedCouponVoucher, setSelectedCouponVoucher] = useState(undefined);
  const {
    title: couponVoucherName,
    discount = 0,
    id: couponVoucherId,
  } = selectedCouponVoucher || {};

  const shopCampaignDiscountAmount = useMemo(() => sumBy(shopCampaignDiscounts, 'amount') || 0, [shopCampaignDiscounts]);
  const productDiscountAmount = useMemo(
    () => sumBy(items, (item) => {
      const { sellPrice, price } = item || {};

      return price - sellPrice;
    }) || 0,
    [items]
  );
  const originalPrice = useMemo(() => sumBy(items, 'price') || 0, [items]);

  useEffect(() => {
    if (
      paymentMethod === PAYMENT_METHOD.ENTERPRISE_CARD_PAY
      && selectedCouponVoucher
    ) setSelectedCouponVoucher(undefined);
  }, [selectedCouponVoucher, paymentMethod]);
  const history = useHistory();

  const onSelectCouponButtonClick = () => {
    if (isEmpty(selectedCouponVoucher)) setIsCouponSelectorOpen(true);
    else setSelectedCouponVoucher(undefined);
  };

  const onCouponSelectorClose = () => {
    setIsCouponSelectorOpen(false);
  };

  const selectCouponVoucher = (couponVoucher) => {
    if (isEmpty(couponVoucher)) return;

    setSelectedCouponVoucher(couponVoucher);

    setIsCouponSelectorOpen(false);
  };

  const onCheckoutButtonClick = async () => {
    try {
      let cardInfo = {};

      if (paymentMethod === PAYMENT_METHOD.TAP_PAY) {
        cardInfo = await new Promise((res) => {
          TPDirect.card.getPrime(({ status = -1, card = {} }) => {
            if (status < 0) throw new Error('您輸入的信用卡資訊有錯誤，請重新輸入！');
            res(card);
          });
        });
      }

      await propsOnCheckoutButtonClick({
        couponVoucherId,
        discount: discount + shopCampaignDiscountAmount,
        paymentMethod,
        ...(paymentMethod === PAYMENT_METHOD.TAP_PAY && {
          paymentInfo: {
            cardNumber: `${cardInfo?.bincode}******${cardInfo?.lastfour}`,
            cardToken: cardInfo?.prime,
            cardType: cardInfo?.type,
          },
        }),
        ...(paymentMethod === PAYMENT_METHOD.ENTERPRISE_CARD_PAY && {
          paymentInfo: {
            enterpriseCardPay: true,
          },
        }),
      });
    } catch (e) {
      setAlert({
        title: '交易失敗',
        message: e.message,
      });
    }
  };

  const onDiscountButtonClick = () => {
    setIsDiscountModalOpened(true);
  };

  const onDiscountModalOkButtonClick = () => {
    setIsDiscountModalOpened(false);
    onCheckoutButtonClick();
  };

  const onDiscountModalCancelButtonClick = () => {
    setIsDiscountModalOpened(false);
  };

  return (
    <div className="fz-container fz-m-auto fz-flex fz-flex-col fz-h-full fz-gap-y-2">
      <CouponSelector
        isOpen={isCouponSelectorOpen}
        onSelect={selectCouponVoucher}
        onClose={onCouponSelectorClose}
      />
      <div className="fz-bg-[#f2f2f2]">
        <LocationGroup readOnly data={dataToDisplay} />
      </div>
      <div className="fz-flex fz-flex-col fz-h-full fz-gap-2 md:fz-flex-row md:fz-items-start">
        <div className="fz-bg-[#f2f2f2] fz-p-2 md:fz-flex-grow">
          <div>付款方式：</div>
          <RadioGroup
            value={paymentMethod}
            onChange={(event) => setPaymentMethod(event.target.value)}
          >
            <FormControlLabel value={PAYMENT_METHOD.LINE_PAY} control={<StyledRadio />} label="以 LINE Pay 付款" />
            <FormControlLabel value={PAYMENT_METHOD.TAP_PAY} control={<StyledRadio />} label="以 信用卡 付款" />
            {
              canUseEnterpriseCardPay
              && (
                <>
                  <FormControlLabel
                    value={PAYMENT_METHOD.ENTERPRISE_CARD_PAY}
                    control={<Radio />}
                    label={(
                      <>
                        {'以 企業員工卡 付款 '}
                        {
                          !hasUserVerifiedEnterpriseCard
                          && (
                          <StyledButton onClick={() => history.push('/user/profile')}>
                            綁定員工卡
                          </StyledButton>
                          )
                        }
                      </>
                    )}
                    disabled={!hasUserVerifiedEnterpriseCard}
                  />
                  {
                    hasUserVerifiedEnterpriseCard && (
                      <div className={classes.paymentHint}>
                        變更員工卡資訊請至會員中心「編輯會員資料」
                      </div>
                    )
                  }
                </>
              )
            }
          </RadioGroup>
          {
            paymentMethod === PAYMENT_METHOD.TAP_PAY && <CreditCardField fieldStyle={{ background: '#FFFFFF' }} />
          }
        </div>
        <div className="fz-sticky fz-bottom-0 fz-flex fz-flex-col fz-mt-auto fz-bg-gray-100 md:fz-bg-transparent md:fz-mt-0 md:fz-w-[300px] md:fz-gap-y-2">
          <div className="fz-hidden md:fz-block">訂單摘要</div>
          {paymentMethod !== PAYMENT_METHOD.ENTERPRISE_CARD_PAY && (
          <div className="fz-flex fz-items-center fz-justify-between fz-p-2 md:fz-p-0">
            <div className={classes.couponText}>優惠券</div>
            <div className={classes.couponField}>
              {couponVoucherName && <div>{couponVoucherName}</div>}
              <Button
                className={classes.selectCouponButton}
                onClick={onSelectCouponButtonClick}
                type={!isEmpty(selectedCouponVoucher) ? 'secondary' : ''}
              >
                {`${isEmpty(selectedCouponVoucher) ? '選擇優惠券' : '移除'}`}
              </Button>
            </div>
          </div>
          )}
          <div className="fz-h-14 fz-flex fz-gap-x-4 fz-gap-y-2 fz-justify-end fz-items-center md:fz-flex-col md:fz-h-auto md:fz-items-stretch">
            {(!isEmpty(shopCampaignDiscounts) || productDiscountAmount !== 0 || discount !== 0) && (
            <div className="fz-flex fz-text-sm fz-flex-col md:fz-text-base md:fz-font-bold">
              <div className="fz-gap-x-2 fz-justify-between md:fz-flex">
                <span>訂單優惠</span>
                <span className="fz-text-onSale">{`$${min([shopCampaignDiscountAmount + productDiscountAmount + discount, originalPrice])}`}</span>
              </div>
              <ButtonBase
                className="
                fz-flex
                fz-items-center
                !fz-border
                !fz-border-solid
                !fz-border-gray-400
                !fz-rounded
              "
                onClick={onDiscountButtonClick}
              >
                <span className="fz-bg-center fz-bg-contain fz-bg-no-repeat fz-w-4 fz-h-4" style={{ backgroundImage: `url(${calculatorIcon})` }} />
                <span>優惠試算</span>
              </ButtonBase>
            </div>
            )}
            <div className="fz-flex fz-gap-x-2 fz-font-bold fz-justify-between">
              <span>
                總金額
              </span>
              <span className="fz-text-onSale">
                {`$${max([originalPrice - shopCampaignDiscountAmount - productDiscountAmount - (discount || 0), 0])}`}
              </span>
            </div>
            <Button
              className="!fz-text-white fz-self-stretch fz-flex-shrink-0"
              variant="contained"
              color="secondary"
              onClick={onCheckoutButtonClick}
            >
              送出訂單
            </Button>
          </div>
        </div>
      </div>
      <DiscountModal
        couponDiscount={selectedCouponVoucher}
        hideOkButton
        isOpened={isDiscountModalOpened}
        itemAmount={size(items)}
        onCancelButtonClick={onDiscountModalCancelButtonClick}
        onOkButtonClick={onDiscountModalOkButtonClick}
        originalPrice={originalPrice}
        productDiscountAmount={productDiscountAmount}
        shopCampaignDiscounts={shopCampaignDiscounts}
      />
      {alert && (
      <Alert
        isOpen
        title={alert?.title || ''}
        content={alert?.message || ''}
        buttons={alert?.buttons || (
          <Button fullWidth onClick={() => setAlert(undefined)}>
            <Box fontWeight="fontWeightBold" color="support.main">
              確認
            </Box>
          </Button>
        )}
      />
      )}
    </div>
  );
};
